import { DirectDebitPaymentModel } from "./models/payment.model";

export type EzyPayDirectDebitField =
  | "accountHolderName"
  | "accountNumber"
  | "bankNumber"
  | "branchNumber"
  | "suffixNumber"
  | "countryCode"
  | "termAndConditionAgreed";

export type EzyPayDirectDebitValidationError =
  | "accountHolderNameRequired"
  | "accountNumberInvalid"
  | "bankNumberInvalid"
  | "branchNumberInvalid"
  | "suffixNumberInvalid"
  | "countryCodeRequired"
  | "termAndConditionAgreedRequired";

export interface EzyPayDirectDebitValidationResult {
  directDebit: DirectDebitPaymentModel;
  errors: Partial<
    Record<EzyPayDirectDebitField, EzyPayDirectDebitValidationError>
  >;
  isValid: boolean;
}

const NUMERIC_BANK_FIELDS = [
  "accountNumber",
  "bankNumber",
  "branchNumber",
  "suffixNumber",
] as const;

/**
 * WebApps accepts common visual separators in bank details, but EzyPay's vault API expects the
 * compact numeric representation. Keep this transformation shared so browser and server validation agree.
 */
export function normalizeEzyPayDirectDebit(
  directDebit: DirectDebitPaymentModel
): DirectDebitPaymentModel {
  const normalized = { ...directDebit };

  // Requests can bypass the number-only UI or populate fields programmatically.
  NUMERIC_BANK_FIELDS.forEach((field) => {
    if (typeof normalized[field] === "string")
      normalized[field] = normalized[field].replace(/[\s-]/g, "");
  });

  if (typeof normalized.countryCode === "string") {
    normalized.countryCode = normalized.countryCode.trim().toUpperCase();
  }

  if (normalized.countryCode === "AU") {
    delete normalized.branchNumber;
    delete normalized.suffixNumber;
  }

  return normalized;
}

export function validateEzyPayDirectDebit(
  directDebit: DirectDebitPaymentModel
): EzyPayDirectDebitValidationResult {
  const rawDirectDebit =
    directDebit && typeof directDebit === "object"
      ? directDebit
      : ({} as DirectDebitPaymentModel);
  const normalized = normalizeEzyPayDirectDebit(rawDirectDebit);
  const errors: EzyPayDirectDebitValidationResult["errors"] = {};
  const accountHolderName = stringValue(normalized.accountHolderName);
  const accountNumber = stringValue(normalized.accountNumber);
  const bankNumber = stringValue(normalized.bankNumber);
  const branchNumber = stringValue(normalized.branchNumber);
  const suffixNumber = stringValue(normalized.suffixNumber);

  if (!accountHolderName?.trim())
    errors.accountHolderName = "accountHolderNameRequired";
  if (
    !isDigits(accountNumber) ||
    !accountNumber ||
    accountNumber.length < 5 ||
    accountNumber.length > 10
  ) {
    errors.accountNumber = "accountNumberInvalid";
  }

  if (!stringValue(normalized.countryCode)?.trim()) {
    errors.countryCode = "countryCodeRequired";
  } else if (normalized.countryCode === "AU") {
    if (!isDigits(bankNumber, 6)) errors.bankNumber = "bankNumberInvalid";
  } else if (normalized.countryCode === "NZ") {
    if (!isDigits(bankNumber, 2)) errors.bankNumber = "bankNumberInvalid";
    if (!isDigits(branchNumber, 4)) errors.branchNumber = "branchNumberInvalid";
    if (!isDigits(suffixNumber, [2, 3]))
      errors.suffixNumber = "suffixNumberInvalid";
  }

  if (
    (rawDirectDebit as Record<string, unknown>).termAndConditionAgreed !== true
  ) {
    errors.termAndConditionAgreed = "termAndConditionAgreedRequired";
  }

  return {
    directDebit: normalized,
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function isDigits(
  value: string | undefined,
  length?: number | number[]
): boolean {
  if (!value || !/^\d+$/.test(value)) return false;
  if (length === undefined) return true;
  return Array.isArray(length)
    ? length.includes(value.length)
    : value.length === length;
}
